"use client"
import { createContext, useContext } from "react"
import { tv, type VariantProps } from "tailwind-variants"
import { classNames } from "../../utils"
import { Avatar, type AvatarProps } from "../Avatar"
import {
  type DefaultItemProps,
  Item,
  ItemAction,
  ItemContent,
  ItemDescription,
  ItemSide,
  ItemTitle,
} from "../Item"
import type { TextBodyProps } from "../Text"
import type { VerticalAlignedProps } from "../VerticalAligned"
import {
  LOCKUP_SIZES,
  type LockupSize,
  lockupSizeVariants,
} from "./lockup-size-variants"

type LockupContextValue = {
  size: LockupSize
}

const LockupContext = createContext<LockupContextValue | null>(null)

const useLockup = () => {
  const context = useContext(LockupContext)
  if (!context) {
    throw new Error(
      "Lockup subcomponents must be used within a Lockup component",
    )
  }
  return context
}

const lockupVariants = tv({
  base: "flex w-full items-center",
  variants: {
    size: {
      xs: "gap-1.5",
      sm: "gap-2",
      md: "gap-3",
      lg: "gap-3",
      xl: "gap-3",
      "2xl": "gap-3",
      "3xl": "gap-4",
    },
  },
  defaultVariants: {
    size: "md",
  },
})

export type LockupProps = DefaultItemProps & VariantProps<typeof lockupVariants>

/**
 * Lockup component - A thin wrapper around Item that provides size-aware styling
 *
 * @example
 * ```tsx
 * <Lockup size="md">
 *   <LockupAvatar src={imageUrl} />
 *   <LockupContent>
 *     <LockupTitle>Item Name</LockupTitle>
 *     <LockupDescription>Collection Name</LockupDescription>
 *   </LockupContent>
 *   <LockupSide>
 *     <LockupTitle>$1,234</LockupTitle>
 *   </LockupSide>
 * </Lockup>
 * ```
 */
export function Lockup({
  children,
  className,
  size = "md",
  ...props
}: LockupProps) {
  return (
    <LockupContext.Provider value={{ size }}>
      <Item
        className={classNames(lockupVariants({ size }), className)}
        variant="unstyled"
        {...props}
      >
        {children}
      </Item>
    </LockupContext.Provider>
  )
}

export type LockupAvatarProps = AvatarProps

/**
 * LockupAvatar - Avatar component that automatically sizes based on parent Lockup size
 *
 * @example
 * ```tsx
 * <Lockup size="md">
 *   <LockupAvatar src={imageUrl} />
 * </Lockup>
 * ```
 */
export function LockupAvatar({ className, size, ...props }: LockupAvatarProps) {
  const { size: contextSize } = useLockup()
  const { image } = lockupSizeVariants({ size: contextSize })

  return (
    <Avatar
      className={classNames(!size && image(), className)}
      size={size ?? LOCKUP_SIZES[contextSize].image}
      {...props}
    />
  )
}

export type LockupContentProps = VerticalAlignedProps

/**
 * LockupContent - Main content area containing title and description
 *
 * @example
 * ```tsx
 * <LockupContent>
 *   <LockupTitle>Item Name</LockupTitle>
 *   <LockupDescription>Collection Name</LockupDescription>
 * </LockupContent>
 * ```
 */
export function LockupContent(props: LockupContentProps) {
  return <ItemContent {...props} />
}

export type LockupTitleProps = Omit<TextBodyProps, "size"> & {
  className?: string
}

/**
 * LockupTitle - Primary text line (name, label, etc.)
 *
 * @example
 * ```tsx
 * <LockupTitle>Bored Ape #1234</LockupTitle>
 * ```
 */
export function LockupTitle({ className, ...props }: LockupTitleProps) {
  const { size } = useLockup()
  const { text } = lockupSizeVariants({ size })
  return <ItemTitle className={classNames(text(), className)} {...props} />
}

export type LockupDescriptionProps = Omit<TextBodyProps, "size"> & {
  className?: string
}

/**
 * LockupDescription - Secondary text line (collection, metadata, etc.)
 *
 * @example
 * ```tsx
 * <LockupDescription>Bored Ape Yacht Club</LockupDescription>
 * ```
 */
export function LockupDescription({
  className,
  ...props
}: LockupDescriptionProps) {
  const { size } = useLockup()
  const { text } = lockupSizeVariants({ size })
  return (
    <ItemDescription className={classNames(text(), className)} {...props} />
  )
}

export type LockupSideProps = VerticalAlignedProps

/**
 * LockupSide - Right-aligned content area (price, stats, etc.)
 *
 * @example
 * ```tsx
 * <LockupSide>
 *   <LockupTitle>$1,234</LockupTitle>
 *   <LockupDescription>Floor</LockupDescription>
 * </LockupSide>
 * ```
 */
export function LockupSide(props: LockupSideProps) {
  return <ItemSide {...props} />
}

export type LockupActionProps = VerticalAlignedProps

/**
 * LockupAction - Action button area (far right)
 *
 * @example
 * ```tsx
 * <LockupAction>
 *   <Button icon={MoreIcon} size="sm" variant="ghost" />
 * </LockupAction>
 * ```
 */
export function LockupAction(props: LockupActionProps) {
  return <ItemAction {...props} />
}
